import { createRng, rngShuffle } from '../rng/rng.js';
import { initBoard } from './board.js';
import { PROVISIONAL_START_ENERGY, PROVISIONAL_START_ENERGY_MAX, PROVISIONAL_START_HAND_SIZE, PROVISIONAL_START_HP, } from './defaults.js';
import { effectIdOf, instanceIdOf } from './ids.js';
import { makeCardInstance } from './instance.js';
import { CURRENT_STATE_VERSION, } from './types.js';
function comparePlayerIds(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function initialRng(seed) {
    if (typeof seed === 'number')
        return createRng(seed);
    if (typeof seed !== 'object' ||
        seed === null ||
        seed.algo !== 'mulberry32' ||
        !Number.isInteger(seed.state) ||
        seed.state < 0 ||
        seed.state > 0xffff_ffff) {
        throw new Error('initState: seed должен быть целым числом или валидным RngState mulberry32');
    }
    return { algo: 'mulberry32', state: seed.state >>> 0 };
}
function assertPlainRecord(value, where) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`${where} должен быть JSON-объектом`);
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new Error(`${where} должен быть обычным JSON-объектом`);
    }
    for (const key of Reflect.ownKeys(value)) {
        if (typeof key !== 'string')
            throw new Error(`${where} содержит symbol key`);
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        if (descriptor?.enumerable !== true || !('value' in descriptor)) {
            throw new Error(`${where}.${key} должен быть обычным enumerable JSON-полем`);
        }
    }
}
function assertJsonValue(value, where, ancestors = new Set()) {
    if (value === null || typeof value === 'string' || typeof value === 'boolean')
        return;
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error(`${where} должен содержать только конечные JSON-числа`);
        return;
    }
    if (typeof value !== 'object')
        throw new Error(`${where} содержит несериализуемое значение`);
    if (ancestors.has(value))
        throw new Error(`${where} содержит циклическую JSON-ссылку`);
    ancestors.add(value);
    if (Array.isArray(value)) {
        if (Object.getPrototypeOf(value) !== Array.prototype) {
            throw new Error(`${where} должен быть обычным JSON-массивом`);
        }
        const allowedKeys = new Set(['length', ...value.map((_entry, index) => String(index))]);
        for (const key of Reflect.ownKeys(value)) {
            if (typeof key !== 'string' || !allowedKeys.has(key)) {
                throw new Error(`${where} содержит нестандартное поле массива`);
            }
        }
        for (let index = 0; index < value.length; index += 1) {
            const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
            if (descriptor?.enumerable !== true || !('value' in descriptor)) {
                throw new Error(`${where}[${index}] должен быть обычным JSON-элементом`);
            }
            assertJsonValue(descriptor.value, `${where}[${index}]`, ancestors);
        }
    }
    else {
        assertPlainRecord(value, where);
        for (const [key, entry] of Object.entries(value)) {
            assertJsonValue(entry, `${where}.${key}`, ancestors);
        }
    }
    ancestors.delete(value);
}
function normalizeStartingEffect(value, playerId, index, registry) {
    const where = `initState: startingEffects[${index}] игрока "${playerId}"`;
    assertPlainRecord(value, where);
    const allowed = new Set(['defId', 'vars', 'statMods']);
    for (const key of Object.keys(value)) {
        if (!allowed.has(key))
            throw new Error(`${where} содержит неизвестное поле "${key}"`);
    }
    const defId = value.defId;
    if (typeof defId !== 'string' || defId === '' || !registry.has(defId)) {
        throw new Error(`${where}: неизвестный defId "${String(defId)}"`);
    }
    if (registry.get(defId)?.type !== 'EFFECT') {
        throw new Error(`${where}: defId "${defId}" должен ссылаться на EFFECT definition`);
    }
    let vars;
    if (value.vars !== undefined) {
        assertPlainRecord(value.vars, `${where}.vars`);
        assertJsonValue(value.vars, `${where}.vars`);
        vars = structuredClone(value.vars);
    }
    let statMods;
    if (value.statMods !== undefined) {
        if (!Array.isArray(value.statMods))
            throw new Error(`${where}.statMods должен быть массивом`);
        if (Object.getPrototypeOf(value.statMods) !== Array.prototype) {
            throw new Error(`${where}.statMods должен быть обычным массивом`);
        }
        const allowedKeys = new Set([
            'length',
            ...Array.from({ length: value.statMods.length }, (_entry, modIndex) => String(modIndex)),
        ]);
        for (const key of Reflect.ownKeys(value.statMods)) {
            if (typeof key !== 'string' || !allowedKeys.has(key)) {
                throw new Error(`${where}.statMods содержит нестандартное поле массива`);
            }
        }
        for (let modIndex = 0; modIndex < value.statMods.length; modIndex += 1) {
            const descriptor = Object.getOwnPropertyDescriptor(value.statMods, String(modIndex));
            if (descriptor?.enumerable !== true || !('value' in descriptor)) {
                throw new Error(`${where}.statMods[${modIndex}] должен быть обычным элементом массива`);
            }
        }
        statMods = value.statMods.map((candidate, modIndex) => {
            const modWhere = `${where}.statMods[${modIndex}]`;
            assertPlainRecord(candidate, modWhere);
            const keys = Object.keys(candidate);
            if (keys.length !== 3 || keys.some((key) => !['stat', 'op', 'value'].includes(key))) {
                throw new Error(`${modWhere} должен содержать только stat/op/value`);
            }
            if (candidate.stat !== 'attack' && candidate.stat !== 'maxHealth') {
                throw new Error(`${modWhere}.stat должен быть attack или maxHealth`);
            }
            if (candidate.op !== 'add' && candidate.op !== 'mul' && candidate.op !== 'set') {
                throw new Error(`${modWhere}.op должен быть add, mul или set`);
            }
            if (typeof candidate.value !== 'number' || !Number.isFinite(candidate.value)) {
                throw new Error(`${modWhere}.value должен быть конечным числом`);
            }
            return { stat: candidate.stat, op: candidate.op, value: Object.is(candidate.value, -0) ? 0 : candidate.value };
        });
    }
    return {
        defId,
        ...(vars !== undefined ? { vars } : {}),
        ...(statMods !== undefined ? { statMods } : {}),
    };
}
function assignSides(players) {
    const explicit = players.filter((player) => player.side !== undefined);
    if (explicit.length === 2 && explicit[0]?.side === explicit[1]?.side) {
        throw new Error(`initState: сторона "${explicit[0]?.side}" назначена двум игрокам`);
    }
    if (explicit.length === 1) {
        const fixed = explicit[0];
        const other = players.find((player) => player !== fixed);
        const assigned = [
            { ...fixed, side: fixed.side },
            { ...other, side: fixed.side === 'A' ? 'B' : 'A' },
        ];
        return assigned.sort((a, b) => (a.side === b.side ? 0 : a.side === 'A' ? -1 : 1));
    }
    if (explicit.length === 2) {
        return players
            .map((player) => ({ ...player, side: player.side }))
            .sort((a, b) => (a.side === b.side ? 0 : a.side === 'A' ? -1 : 1));
    }
    return [...players]
        .sort((a, b) => comparePlayerIds(a.playerId, b.playerId))
        .map((player, index) => ({ ...player, side: index === 0 ? 'A' : 'B' }));
}
/** Детерминированно построить чистый стартовый GameState (G4/D7/D11). */
export function initState(params) {
    if (!Array.isArray(params.players) || params.players.length !== 2) {
        throw new Error('initState: требуется ровно два игрока');
    }
    if (params.players.some((player) => typeof player !== 'object' || player === null)) {
        throw new Error('initState: каждый игрок должен быть объектом PlayerInit');
    }
    if (!(params.registry instanceof Map)) {
        // ReadonlyMap структурен, но рантайм-контракт требует map-подобный registry.
        if (typeof params.registry?.get !== 'function' || typeof params.registry?.has !== 'function') {
            throw new Error('initState: registry должен быть CardRegistry');
        }
    }
    const ids = params.players.map((player) => player.playerId);
    if (ids.some((id) => typeof id !== 'string' || id === '')) {
        throw new Error('initState: playerId должен быть непустой строкой');
    }
    if (new Set(ids).size !== 2)
        throw new Error(`initState: playerId должны быть уникальны`);
    if (params.players.some((player) => player.side !== undefined && player.side !== 'A' && player.side !== 'B')) {
        throw new Error('initState: side должен быть A или B');
    }
    if (params.matchId !== undefined && (typeof params.matchId !== 'string' || params.matchId === '')) {
        throw new Error('initState: matchId должен быть непустой строкой');
    }
    for (const player of params.players) {
        if (!Array.isArray(player.deck))
            throw new Error(`initState: deck игрока "${player.playerId}" должен быть массивом`);
        for (const defId of player.deck) {
            if (typeof defId !== 'string' || defId === '' || !params.registry.has(defId)) {
                throw new Error(`initState: неизвестный defId "${String(defId)}" в колоде игрока "${player.playerId}"`);
            }
        }
        if (player.startingEffects !== undefined && !Array.isArray(player.startingEffects)) {
            throw new Error(`initState: startingEffects игрока "${player.playerId}" должен быть массивом`);
        }
    }
    const ordered = assignSides(params.players);
    const startingEffects = new Map();
    for (const player of ordered) {
        startingEffects.set(player.playerId, Object.freeze((player.startingEffects ?? []).map((effect, index) => normalizeStartingEffect(effect, player.playerId, index, params.registry))));
    }
    const seedRng = initialRng(params.seed);
    let rng = seedRng;
    let nextSeq = 1;
    const instances = {};
    const players = {};
    for (const init of ordered) {
        // Сначала перемешиваем определения и только потом выдаём публично стабильные
        // instanceId/seq. Иначе iN однозначно соответствовал позиции defId в
        // известном списке колоды, и скрытая armed-карта раскрывалась по своему id.
        const [shuffledDefinitions, advanced] = rngShuffle(rng, init.deck);
        rng = advanced;
        const materialized = [];
        for (const defId of shuffledDefinitions) {
            const def = params.registry.get(defId);
            const seq = nextSeq;
            const id = instanceIdOf(seq);
            nextSeq += 1;
            const attack = def.baseStats?.attack ?? 0;
            const health = def.baseStats?.health ?? 1;
            instances[id] = makeCardInstance({
                id,
                seq,
                defId,
                ownerId: init.playerId,
                zone: 'deck',
                stats: { attack, health, maxHealth: health },
            });
            materialized.push(id);
        }
        const hand = materialized.slice(0, PROVISIONAL_START_HAND_SIZE);
        const deck = materialized.slice(PROVISIONAL_START_HAND_SIZE);
        for (const instanceId of hand)
            instances[instanceId].zone = 'hand';
        const player = {
            id: init.playerId,
            side: init.side,
            health: PROVISIONAL_START_HP,
            resources: {
                energy: PROVISIONAL_START_ENERGY,
                energyMax: PROVISIONAL_START_ENERGY_MAX,
                temporaryEnergy: 0,
                temporaryEnergyRound: null,
            },
            ordinaryTokenDeaths: 0,
            deck,
            hand,
            discard: [],
            armedReactions: [],
            staged: [],
            nextPlanSeq: 1,
            ready: false,
        };
        // PlayerId — произвольная непустая строка. defineProperty сохраняет даже
        // ключ "__proto__" как обычное enumerable JSON-поле, не меняя прототип.
        Object.defineProperty(players, init.playerId, {
            value: player,
            enumerable: true,
            configurable: true,
            writable: true,
        });
    }
    const effects = [];
    let nextEffectSeq = 1;
    for (const init of ordered) {
        for (const input of startingEffects.get(init.playerId) ?? []) {
            const seq = nextEffectSeq;
            effects.push({
                id: effectIdOf(seq),
                seq,
                defId: input.defId,
                scope: { kind: 'player', playerId: init.playerId },
                persistent: true,
                ...(input.statMods !== undefined ? { statMods: structuredClone([...input.statMods]) } : {}),
                vars: structuredClone(input.vars ?? {}),
            });
            nextEffectSeq += 1;
        }
    }
    return {
        version: CURRENT_STATE_VERSION,
        matchId: params.matchId ??
            `match:${seedRng.state}:${JSON.stringify(ordered.map((player) => player.playerId))}`,
        rng,
        round: 1,
        phase: 'planning',
        status: { phase: 'active' },
        players,
        board: initBoard(),
        instances,
        effects,
        attachments: [],
        captureReservations: [],
        pendingExtraAttacks: [],
        rules: {},
        nextInstanceSeq: nextSeq,
        nextEffectSeq,
        timedDestroys: [],
        nextTimedDestroySeq: 1,
        nextHackSeq: 1,
        nextAttachmentSeq: 1,
        nextCaptureSeq: 1,
        nextExtraAttackSeq: 1,
    };
}
//# sourceMappingURL=initState.js.map